import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Modal from 'components/core/Modal';
import ModalDialog from 'components/core/ModalDialog';
import ModalClose from 'components/core/ModalClose';
import Stack from 'components/core/Stack';
import Select from 'components/core/Select';
import Typography from 'components/core/Typography';
import Sheet from 'components/core/Sheet';
import Button from 'components/core/Button';
import Input from 'components/core/Input';
import Switch from 'components/core/Switch';
import LookupTableInput from 'components/LookupTableInput';

const OPERATORS = {
  String: ['equals', 'contains'],
  Number: ['equals', 'greater than', 'less than', 'between'],
  Date: ['equals', 'before', 'after', 'between'],
  Boolean: ['equals'],
  Pointer: ['equals', 'not equals']
};

const CLASS_MAP = {
    'Name': 'names',
    'Substantiation': 'substantiations',
    'LocDeterminization': 'locDeterminization',
    'Substrate': 'substrates',
    'ObservationQuality': 'qualities',
    'Location': 'locations',
    'Person': 'persons',
    'Event': 'events',
    'SourceOwned': 'sourceOwneds',
    'Repository': 'repositories',
    'NoteType': 'noteTypes',
    'EvidenceFormat': 'evidenceFormats',
    'Keyword': 'keywords',
    'Town': 'towns'
}

const CriterionDialog = ({ 
    criterion,
    fieldOptions,
    onClose,
    onUpdateCriterion,
    observationOptions,
    personList
}) => {
    const [localCriterion, setLocalCriterion] = useState(criterion || { field: null, operator: null, value: null });
    const [rangeValues, setRangeValues] = useState({ start: '', end: '' });
    
    useEffect(() => {
        if (criterion) {
            setLocalCriterion(criterion);
            if (criterion.operator === 'between' && Array.isArray(criterion.value)) {
                setRangeValues({ start: criterion.value[0], end: criterion.value[1] });
            }
        }
    }, [criterion]);

    if (!criterion) return null;

    const getFieldType = (fieldName) => {
        const field = fieldOptions.find(f => f.field === fieldName);
        if (!field) return null;
        return typeof field.type === 'string' ? field.type : field.type.type;
    };

    const getOperators = (fieldName) => {
        const fieldType = getFieldType(fieldName);
        return OPERATORS[fieldType] || [];
    };

    const handleLocalUpdate = (key, value) => {
        if (key === 'operator' && value !== 'between') {
            setRangeValues({ start: '', end: '' });
        }

        if (key === 'field') {
            const fieldData = fieldOptions.find(f => f.field === value);
            if (fieldData.type === 'Boolean') {
                return setLocalCriterion({
                    ...localCriterion,
                    [key]: value,
                    value: false
                });
            }
        }
        setLocalCriterion({
            ...localCriterion,
            [key]: value
        })
    };

    const handleRangeUpdate = (type, value) => {
        const newRangeValues = { ...rangeValues, [type]: value };
        setRangeValues(newRangeValues);
        
        if (newRangeValues.start !== '' && newRangeValues.end !== '') {
            handleLocalUpdate('value', [newRangeValues.start, newRangeValues.end]);
        }
    };

    const isValid = () => {
        if (!localCriterion.field || !localCriterion.operator) return false;
        
        if (localCriterion.operator === 'between') {
            return rangeValues.start !== '' && rangeValues.end !== '';
        }
        
        return localCriterion.value !== undefined && localCriterion.value !== '';
    };

    const handleDone = () => {
        if (isValid()) {
            const field = fieldOptions.find(f => f.field === localCriterion.field);
            let finalCriterion = { ...localCriterion };
            
            if (field?.type === 'Date' && localCriterion.operator !== 'between') {
                const value = localCriterion.value;
                if (typeof value === 'string' && value) {
                    if (/^\d{4}$/.test(value)) {
                        finalCriterion.value = `${value}-01-01`;
                    } else if (/^\d{4}-\d{2}$/.test(value)) {
                        finalCriterion.value = `${value}-01`;
                    }
                }
            } else if (field?.type === 'Date' && localCriterion.operator === 'between') {
                const processDateValue = (val) => {
                    if (typeof val === 'string' && val) {
                        if (/^\d{4}$/.test(val)) {
                            return `${val}-01-01`;
                        } else if (/^\d{4}-\d{2}$/.test(val)) {
                            return `${val}-01`;
                        }
                    }
                    return val;
                };
                
                if (Array.isArray(localCriterion.value)) {
                    finalCriterion.value = [
                        processDateValue(localCriterion.value[0]),
                        processDateValue(localCriterion.value[1])
                    ];
                }
            }
            
            onUpdateCriterion(finalCriterion);
        }
    };

    const renderValueInput = () => {
        const field = fieldOptions.find(f => f.field === localCriterion.field);
        if (!field) return null;

        if (localCriterion.operator === 'between') {
            if (field.type === 'Date') {
                return (
                    <Stack spacing={2}>
                        <Input
                            label="Start Value"
                            value={rangeValues.start}
                            onChange={(value) => handleRangeUpdate('start', value)}
                            required
                        />
                        <Input
                            label="End Value"
                            value={rangeValues.end}
                            onChange={(value) => handleRangeUpdate('end', value)}
                            required
                        />
                    </Stack>
                );
            } else {
                return (
                    <Stack spacing={2}>
                        <Input
                            type="number"
                            label="Start Value"
                            value={rangeValues.start}
                            onChange={(value) => handleRangeUpdate('start', value)}
                            required
                        />
                        <Input
                            type="number"
                            label="End Value"
                            value={rangeValues.end}
                            onChange={(value) => handleRangeUpdate('end', value)}
                            required
                        />
                    </Stack>
                );
            }
        }

        if (field?.type?.type === 'Pointer') {
            const targetClass = field?.type?.targetClass;
            let options = observationOptions[CLASS_MAP[targetClass]];
            let getOptionLabel = (option) => option?.get('label');
            
            if (targetClass === 'Person') {
                options = personList;
                getOptionLabel = (option) => {
                    const person = option.get('person');
                    const personOfficialName = `${person?.get('firstName')} ${person?.get('lastName')}`; 
                    const thisName = `${option?.get('name')}`;
                    if (personOfficialName === thisName) {
                        return personOfficialName;
                    } else {
                        return `${option?.get('name')} (${personOfficialName})`
                    }
                };
            } else if (targetClass === 'Name' || targetClass === 'Event') {
                getOptionLabel = (option) => option?.get('name');
            }
            
            return (
                <LookupTableInput
                    label="Value"
                    value={localCriterion.value}
                    onChange={(value) => handleLocalUpdate('value', value)}
                    options={options || []}
                    getOptionLabel={getOptionLabel}
                    suppressForm={true}
                    required
                />
            );
        } else if (field.type === 'Boolean') {
            return (
                <Switch
                    label="Value"
                    checked={localCriterion.value}
                    onChange={(e) => handleLocalUpdate('value', e.target.checked || false)}
                />
            );
        } else if (field.type === 'Date') {
            return (
                <Input
                    label="Value" 
                    value={localCriterion.value}
                    onChange={(value) => handleLocalUpdate('value', value)}
                    required
                />
            );
        } else if (field.type === 'Number') {
            return (
                <Input
                    type="number"
                    label="Value"
                    value={localCriterion.value}
                    onChange={(value) => handleLocalUpdate('value', value)}
                    required
                />
            );
        }

        return (
            <Input
                label="Value"
                value={localCriterion.value}
                onChange={(value) => handleLocalUpdate('value', value)}
                required
            />
        );
    };

    return (
        <Modal open={Boolean(criterion)} onClose={onClose}>
        <ModalDialog layout='center' sx={{ width: '50vw' }}>
            <ModalClose />
            <Stack spacing={3} sx={{ p: 3 }}>
            <Typography variant="h4">Add Query Criteria</Typography>

            <Select
                label="Select Field"
                value={localCriterion.field}
                onChange={(value) => handleLocalUpdate('field', value)}
                options={fieldOptions.map(f => f.field)}
                labels={fieldOptions.reduce((acc, f) => ({
                ...acc,
                [f.field]: f.label
                }), {})}
                required
            />

            {localCriterion.field && (
                <Select
                    label="Select Operator"
                    value={localCriterion.operator}
                    onChange={(value) => handleLocalUpdate('operator', value)}
                    options={getOperators(localCriterion.field)}
                    labels={getOperators(localCriterion.field).reduce((acc, op) => ({
                        ...acc,
                        [op]: op
                    }), {})}
                    required
                />
            )}

            {localCriterion.operator && renderValueInput()}

            <Sheet sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button onClick={handleDone} disabled={!isValid()}>
                    Done
                </Button>
            </Sheet>
            </Stack>
        </ModalDialog>
        </Modal>
    );
};

CriterionDialog.propTypes = {
    criterion: PropTypes.shape({
        field: PropTypes.string,
        operator: PropTypes.string,
        value: PropTypes.any
    }),
    fieldOptions: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                type: PropTypes.string,
                targetClass: PropTypes.string
            })
        ]).isRequired
    })).isRequired,
    onClose: PropTypes.func.isRequired,
    onUpdateCriterion: PropTypes.func.isRequired,
    observationOptions: PropTypes.object,
    personList: PropTypes.array
};

export default CriterionDialog;